// Compose / Reply dialog for internal mail.
//
// Supports a rich-text body (bold / colour / images, see RichTextEditor) and can
// be MINIMISED into a floating bar so the user can keep browsing the portal and
// return to the same draft. State lives here and persists while the dialog is
// mounted (the global ComposeProvider keeps it mounted across route changes).
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Paperclip, Send, X, Loader2, Flag, FileText, EyeOff, Minus, Mail,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import RecipientPicker from '@/components/mail/RecipientPicker';
import RichTextEditor from '@/components/mail/RichTextEditor';
import {
  MAIL_ACCEPT, MAX_MAIL_BODY_CHARS, fileToAttachment, prettySize, fileKind, htmlToText,
} from '@/lib/mail';

export const ComposeDialog = ({
  open,
  onOpenChange,
  onSent,
  initial,
  minimized = false,
  onMinimize,
  onRestore,
}) => {
  const { effectiveCompanyId } = useAuth();
  const [recipients, setRecipients] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState(false);
  const [disappearing, setDisappearing] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  // Re-seed the draft only when a NEW compose is opened (open false→true) or the
  // seed changes — never on minimise/restore, so the draft survives.
  const seedKey = `${open ? 'o' : 'c'}|${initial?.parent_id || ''}|${initial?.reply_kind || ''}|${initial?.subject || ''}`;
  const lastSeed = useRef(null);

  useEffect(() => {
    if (open && lastSeed.current !== seedKey) {
      lastSeed.current = seedKey;
      setRecipients(initial?.recipients || []);
      setSubject(initial?.subject || '');
      setBody(initial?.body || '');
      setPriority(!!initial?.priority);
      setDisappearing(!!initial?.disappearing);
      setAttachments([]);
    }
    if (!open) lastSeed.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seedKey]);

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const f of files) {
      try {
        const att = await fileToAttachment(f);
        setAttachments((prev) => [...prev, att]);
      } catch (err) {
        toast.error(err.message || 'Attachment rejected');
      }
    }
  };
  const removeAttachment = (idx) => setAttachments((prev) => prev.filter((_, i) => i !== idx));

  const send = async () => {
    if (recipients.length === 0) { toast.error('Add at least one recipient'); return; }
    const bodyText = htmlToText(body);
    const hasImage = /<img\b/i.test(body);
    if (!subject.trim() && !bodyText && !hasImage) { toast.error('Add a subject or a message'); return; }
    if ((body || '').length > MAX_MAIL_BODY_CHARS) {
      toast.error('Message is too large — please use fewer or smaller images');
      return;
    }
    setSending(true);
    try {
      await api.post('/mail/compose', {
        subject: subject.trim(),
        body: body || '',
        recipient_ids: recipients.map((r) => r.id),
        priority,
        disappearing,
        attachments: attachments.map((a) => ({ name: a.name, mime: a.mime, data_url: a.data_url })),
        parent_id: initial?.parent_id || null,
        reply_kind: initial?.reply_kind || null,
        company_id: effectiveCompanyId || null,
      });
      toast.success('Message sent');
      onOpenChange(false);
      onSent && onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  // ── Minimised state: a floating bar, page stays fully interactive ────────
  if (minimized) {
    return (
      <div
        className="fixed bottom-4 right-4 z-[220] w-[300px] rounded-lg border border-[var(--divider)] bg-white shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-150"
        data-testid="mail-compose-minimized"
      >
        <button
          type="button"
          onClick={() => onRestore && onRestore()}
          className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
          title="Restore message"
          data-testid="mail-compose-restore"
        >
          <Mail size={15} className="text-[var(--ink-navy)] shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="block text-xs font-semibold text-[var(--ink-navy)] truncate">
              {subject.trim() || 'New message'}
            </span>
            <span className="block text-[11px] text-slate-400 truncate">
              {recipients.length > 0 ? `To: ${recipients.map((r) => r.name).join(', ')}` : 'Draft — click to continue'}
            </span>
          </span>
          {priority && <Flag size={13} className="text-rose-600 shrink-0" />}
        </button>
        <div className="flex items-center gap-1 border-t border-[var(--divider)] px-2 py-1.5 bg-slate-50">
          <Button size="sm" className="luxury-button h-7 px-2 text-xs" onClick={send} disabled={sending} data-testid="mail-compose-minimized-send">
            {sending ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Send size={13} className="mr-1" />} Send
          </Button>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs ml-auto" onClick={() => onRestore && onRestore()} data-testid="mail-compose-minimized-open">
            Open
          </Button>
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
            onClick={() => onOpenChange(false)}
            title="Discard message"
            aria-label="Discard message"
            data-testid="mail-compose-minimized-close"
          >
            <X size={15} />
          </button>
        </div>
      </div>
    );
  }

  // ── Full dialog ──────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="mail-compose-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <DialogTitle>{initial?.parent_id ? 'Reply' : 'New message'}</DialogTitle>
              <DialogDescription>Send an internal message to teammates. Format the text, paste images, attach files, and mark priority if urgent.</DialogDescription>
            </div>
            <button
              type="button"
              onClick={() => onMinimize && onMinimize()}
              className="mt-0.5 mr-6 inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-[var(--ink-navy)] hover:bg-slate-100 transition-colors"
              title="Minimise — keep browsing and come back"
              aria-label="Minimise"
              data-testid="mail-compose-minimize"
            >
              <Minus size={16} />
            </button>
          </div>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500">To</Label>
            <div className="mt-1"><RecipientPicker selected={recipients} onChange={setRecipients} /></div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="mt-1 h-9" data-testid="mail-compose-subject" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Message</Label>
            <div className="mt-1">
              <RichTextEditor
                value={body}
                onChange={setBody}
                placeholder="Write your message… (paste images, use the toolbar to format)"
                testId="mail-compose-body"
                minHeight={200}
              />
            </div>
          </div>

          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="mail-compose-attachments">
              {attachments.map((a, i) => (
                <Badge key={i} variant="secondary" className="gap-1.5 border border-[var(--divider)] bg-[var(--paper-warm)] text-slate-700 py-1">
                  <FileText size={12} className="text-[var(--diamond-icon)]" />
                  <span className="max-w-[160px] truncate">{a.name}</span>
                  <span className="text-[10px] text-slate-400">{fileKind(a.mime, a.name)} · {prettySize(a.size)}</span>
                  <button type="button" onClick={() => removeAttachment(i)} className="hover:text-rose-600" aria-label="Remove attachment"><X size={12} /></button>
                </Badge>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-center gap-3 flex-wrap">
              <input ref={fileRef} type="file" multiple accept={MAIL_ACCEPT} className="hidden" onChange={onPickFiles} data-testid="mail-compose-file-input" />
              <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => fileRef.current?.click()} data-testid="mail-compose-attach-button">
                <Paperclip size={14} className="mr-1.5" /> Attach
              </Button>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                <Checkbox checked={priority} onCheckedChange={(v) => setPriority(!!v)} data-testid="mail-compose-priority" />
                <Flag size={14} className={priority ? 'text-rose-600' : 'text-slate-400'} />
                Priority
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                <Checkbox checked={disappearing} onCheckedChange={(v) => setDisappearing(!!v)} data-testid="mail-compose-disappearing" />
                <EyeOff size={14} className={disappearing ? 'text-rose-600' : 'text-slate-400'} />
                Disappearing Mail
              </label>
            </div>
            {disappearing && (
              <p className="flex items-start gap-1.5 text-[11px] leading-snug text-rose-600/90" data-testid="mail-compose-disappearing-hint">
                <EyeOff size={12} className="mt-0.5 shrink-0" />
                <span>Viewable once — each recipient can read it a single time, then it vanishes from their inbox. It also leaves your Sent box once everyone has read it.</span>
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onMinimize && onMinimize()} disabled={sending} data-testid="mail-compose-minimize-footer">
            <Minus size={15} className="mr-1.5" /> Minimise
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sending}>Discard</Button>
          <Button className="luxury-button" onClick={send} disabled={sending} data-testid="mail-compose-send">
            {sending ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Send size={15} className="mr-1.5" />} Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ComposeDialog;
